/**
 * DatePickerField - 브라우저 기본 datepicker 대신 통일된 디자인의 커스텀 캘린더 팝오버.
 *
 * - 트리거 버튼을 누르면 캘린더가 팝오버로 열린다.
 * - 스크롤되는 모달(overflow-y-auto) 안에서도 잘리지 않도록 팝오버는 createPortal로
 *   document.body에 fixed 포지션으로 렌더하고, 트리거의 위치에 맞춰 좌표를 계산한다.
 * - 미래 예약을 위해 월 이동에 상한이 없다(useYearMonth 훅은 이번 달 초과를 막아 부적합).
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  WEEKDAY_LABELS,
  getMonthMatrix,
  addMonth,
  formatDateLabel,
  type CalendarCell,
} from "@/utils/calendar";

interface DatePickerFieldProps {
  /** "YYYY-MM-DD" */
  value: string;
  onChange: (next: string) => void;
  id?: string;
}

const POPOVER_WIDTH = 288; // w-72
const MARGIN = 8;

/** 오늘(KST) "YYYY-MM-DD". 오늘 강조용. */
function todayKST(): string {
  return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function viewFromValue(value: string): { year: number; month: number } {
  const [y, m] = value.split("-").map(Number);
  if (y && m) return { year: y, month: m };
  const [ty, tm] = todayKST().split("-").map(Number);
  return { year: ty, month: tm };
}

export function DatePickerField({ value, onChange, id }: DatePickerFieldProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState(() => viewFromValue(value));
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const open = () => {
    setView(viewFromValue(value));
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const left = Math.min(
        Math.max(rect.left, MARGIN),
        window.innerWidth - POPOVER_WIDTH - MARGIN,
      );
      setPos({ top: rect.bottom + 4, left });
    }
    setIsOpen(true);
  };

  // 외부 클릭 / Escape / 스크롤·리사이즈 시 닫기
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    const onScrollOrResize = () => setIsOpen(false);

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    // 스크롤 컨테이너(모달) 스크롤도 잡기 위해 capture 단계로 등록
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  const handleSelect = (cell: CalendarCell) => {
    onChange(cell.date);
    setIsOpen(false);
  };

  const goPrevMonth = () => setView((v) => addMonth(v.year, v.month, -1));
  const goNextMonth = () => setView((v) => addMonth(v.year, v.month, 1));

  const today = todayKST();
  const weeks = getMonthMatrix(view.year, view.month);

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-left focus:outline-none focus:border-primary-400"
      >
        <span className={value ? "text-gray-800" : "text-gray-400"}>
          {value ? formatDateLabel(value) : "날짜 선택"}
        </span>
        <span className="text-gray-400">▾</span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[60] bg-white border border-gray-200 rounded-lg shadow-lg p-3"
            style={{ top: pos.top, left: pos.left, width: POPOVER_WIDTH }}
          >
            {/* 월 네비게이션 */}
            <div className="flex items-center justify-between mb-2">
              <button
                type="button"
                onClick={goPrevMonth}
                aria-label="이전 달"
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800 rounded hover:bg-gray-100"
              >
                ‹
              </button>
              <span className="text-sm font-medium text-gray-800">
                {view.year}년 {view.month}월
              </span>
              <button
                type="button"
                onClick={goNextMonth}
                aria-label="다음 달"
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-800 rounded hover:bg-gray-100"
              >
                ›
              </button>
            </div>

            {/* 요일 헤더 */}
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAY_LABELS.map((w) => (
                <div key={w} className="text-center text-xs text-gray-400 py-1">
                  {w}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div className="grid grid-cols-7 gap-0.5">
              {weeks.flat().map((cell) => {
                const isSelected = cell.date === value;
                const isToday = cell.date === today;
                return (
                  <button
                    key={cell.date}
                    type="button"
                    onClick={() => handleSelect(cell)}
                    className={[
                      "min-h-[2.25rem] flex items-center justify-center text-sm rounded",
                      isSelected
                        ? "bg-primary-500 text-white font-medium"
                        : cell.inMonth
                          ? "text-gray-800 hover:bg-primary-50"
                          : "text-gray-300 hover:bg-gray-50",
                      !isSelected && isToday ? "ring-1 ring-primary-400" : "",
                    ].join(" ")}
                  >
                    {cell.day}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
